import { useState, useEffect } from 'react';
import {
	H2, Text, Caption, Spacer, Loader, StatusChip, Button, Sidebar, Tile,
} from '@components';
import { parseAndFormatDate, getFuelLevyTypeLabel } from '@utils';
import { useRateCardFuelLevyHistory } from '@api/RateCards/fuelLevyHistoryApi.ts';
import {
	BUTTON_SIZE, BUTTON_TYPES, CHIP_TYPE, CHIP_SIZE, type IFuelLevyHistoryEntry, type RateCardTableType,
} from '@types';
import styles from './FuelLevyHistoryPanel.module.scss';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export interface FuelLevyHistoryPanelProps {
	rateCardExternalId: string;
	isOpen: boolean;
	onClose: () => void;
	customerName?: string;
	customerType?: RateCardTableType;
}

const ENTRIES_PER_PAGE = 8;

const STATUS_CONFIG: Record<IFuelLevyHistoryEntry['status'], { label: string; chipType: CHIP_TYPE }> = {
	scheduled: { label: 'Scheduled', chipType: CHIP_TYPE.WARN },
	current: { label: 'Current', chipType: CHIP_TYPE.SUCCESS },
	expired: { label: 'Expired', chipType: CHIP_TYPE.NEUTRAL },
};

export const FuelLevyHistoryPanel = (props: FuelLevyHistoryPanelProps) => {
	const { rateCardExternalId, isOpen, onClose, customerName, customerType } = props;
	const { data, isLoading, error, callRateCardFuelLevyHistory } = useRateCardFuelLevyHistory();
	const [currentPage, setCurrentPage] = useState(1);

	const entries: IFuelLevyHistoryEntry[] = Array.isArray(data) ? data : [];
	const totalPages = Math.max(1, Math.ceil(entries.length / ENTRIES_PER_PAGE));
	const startIndex = (currentPage - 1) * ENTRIES_PER_PAGE;
	const paginatedEntries = entries.slice(startIndex, startIndex + ENTRIES_PER_PAGE);

	const handlePrevPage = () => {
		setCurrentPage((prev) => Math.max(1, prev - 1));
	};

	const handleNextPage = () => {
		setCurrentPage((prev) => Math.min(totalPages, prev + 1));
	};

	const title = (
		<div>
			<H2>Fuel Levy History</H2>
			{customerName && <Text>{customerName}</Text>}
		</div>
	);

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className={styles.loadingState} data-testid='@Ofload-FuelLevyHistoryPanel-loading'>
					<Loader size={24} />
				</div>
			);
		}

		if (error) {
			return (
				<div className={styles.errorState} data-testid='@Ofload-FuelLevyHistoryPanel-error'>
					<Text>Failed to load history. Please try again.</Text>
				</div>
			);
		}

		if (entries.length === 0) {
			return (
				<div className={styles.emptyState} data-testid='@Ofload-FuelLevyHistoryPanel-empty'>
					<Text>No history available</Text>
				</div>
			);
		}

		return (
			<>
				<div className={styles.cardList} data-testid='@Ofload-FuelLevyHistoryPanel-card-list'>
					{paginatedEntries.map((entry, i) => (
						<Tile key={`${startIndex + i}`} hasPadding={false} stretch>
							<div className={styles.cardBody}>
								<div className={styles.cardRow}>
									<div className={styles.cardRowLeft}>
										<DescriptionOutlinedIcon className={styles.cardIcon} />
										<Text>Levy: {entry.tariff}%</Text>
										<StatusChip
											text={getFuelLevyTypeLabel(entry.fuel_levy_type, customerType ?? 'admin')}
											type={CHIP_TYPE.INFO}
											size={CHIP_SIZE.SMALL}
											rounded
										/>
									</div>
									<StatusChip
										text={STATUS_CONFIG[entry.status].label}
										type={STATUS_CONFIG[entry.status].chipType}
										size={CHIP_SIZE.SMALL}
										rounded
									/>
								</div>
								<div className={styles.cardRow}>
									<div className={styles.cardRowLeft}>
										<CalendarTodayOutlinedIcon className={styles.cardIcon} />
										<Text>Start date: {parseAndFormatDate(entry.effective_from, 'DATE_WITH_SHORT_MONTH_NAME', 'API_DATE_FORMAT', '—')}</Text>
									</div>
								</div>
							</div>
							<div className={styles.cardFooter} data-testid='@Ofload-FuelLevyHistoryPanel-entry-footer'>
								<Caption>
									Updated by: {entry.changed_by ?? '—'} • {parseAndFormatDate(entry.changed_at, 'DATE_WITH_SHORT_MONTH_NAME', 'ISO', '—')}
								</Caption>
							</div>
						</Tile>
					))}
				</div>
				{entries.length > ENTRIES_PER_PAGE && (
					<div className={styles.pagination} data-testid='@Ofload-FuelLevyHistoryPanel-pagination'>
						<Button
							iconOnly
							Icon={ChevronLeftIcon}
							onClick={handlePrevPage}
							text='Previous'
							type={BUTTON_TYPES.TERTIARY}
							size={BUTTON_SIZE.SMALL}
							disabled={currentPage === 1}
						/>
						<Text data-testid='@Ofload-FuelLevyHistoryPanel-pagination-info'>Page {currentPage} of {totalPages}</Text>
						<Button
							iconOnly
							Icon={ChevronRightIcon}
							onClick={handleNextPage}
							text='Next'
							type={BUTTON_TYPES.TERTIARY}
							size={BUTTON_SIZE.SMALL}
							disabled={currentPage === totalPages}
						/>
					</div>
				)}
			</>
		);
	};

	useEffect(() => {
		if (!isOpen) return;

		setCurrentPage(1);
		callRateCardFuelLevyHistory({ routes: [rateCardExternalId] });
	}, [isOpen, rateCardExternalId]);

	return (
		<Sidebar title={title} open={isOpen} toggleSideBar={onClose} width='25%'>
			<Spacer size={12} />
			{renderContent()}
		</Sidebar>
	);
};
